import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css'; // обязательно подключаем стили

const formatDate = (dateString) => new Date(dateString).toLocaleString();

const statusColor = (status) => {
  if (status === 'added') return 'green';
  if (status === 'removed') return 'red';
  if (status === 'modified') return 'orange';
  return 'inherit';
};

const History = ({ history }) => {
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  // Изначальные ширины колонок
  const [columnWidths, setColumnWidths] = useState({
    userName: 120,
    time: 120,
    name: 150,
    version: 70,
    statusName: 50,
    fileName: 100,
    applicabilityName: 50,
  });

  if (!history || history.length === 0) {
    return null;
  }

  // Функция изменения ширины конкретного столбца
  const handleResize = (key) => (e, { size }) => {
    setColumnWidths((prev) => ({
      ...prev,
      [key]: size.width,
    }));
  };

  // Разбивка данных по страницам
  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Функция определения, был ли параметр добавлен/удалён/изменён
  const renderChange = (before, after) => {
    if (before === after) return null;

    let status = 'modified';
    if (before === null || before === '' || before === undefined) status = 'added';
    if (after === null || after === '' || after === undefined) status = 'removed';

    return { before, after, status };
  };

  // Список колонок
  const headCells = [
    { key: 'userName', content: 'Пользователь' },
    { key: 'time', content: 'Время' },
    { key: 'name', content: 'Наименование' },
    { key: 'version', content: 'Версия' },
    { key: 'statusName', content: 'Статус' },
    { key: 'fileName', content: 'Файл' },
    { key: 'applicabilityName', content: 'Применимость' },
  ];

  // Генерация строк таблицы
  const rows = paginatedHistory.map((entry, index) => {
    const changeCells = ['name', 'version', 'statusName', 'fileName', 'applicabilityName'].map(
      (field) => {
        const before = entry[`${field}Before`];
        const after = entry[`${field}After`];
        const change = renderChange(before, after);
        const content = change?.after || before || '';
        const status = change?.status;
        return (
          <EllipsisTooltipCell
            key={field}
            content={content}
            color={statusColor(status)}
          />
        );
      }
    );

    return (
      <tr key={index}>
        <td style={{ width: columnWidths.userName }}>
          <EllipsisTooltipCell content={entry.userName || ''} />
        </td>
        <td style={{ width: columnWidths.time }}>
          <EllipsisTooltipCell content={formatDate(entry.time) || ''} />
        </td>
        {changeCells.map((cell, i) => (
          <td key={i} style={{ width: columnWidths[headCells[i + 2].key] }}>
            {cell}
          </td>
        ))}
      </tr>
    );
  });

  return (
    <HistoryContainer>
      <TableWrapper>
        <table>
          <thead>
            <tr>
              {headCells.map((cell) => (
                <th key={cell.key} style={{ width: columnWidths[cell.key] }}>
                  <Resizable
                    width={columnWidths[cell.key]}
                    height={0}
                    onResize={handleResize(cell.key)}
                    resizeHandles={['e']}
                  >
                    <HeaderContent>{cell.content}</HeaderContent>
                  </Resizable>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableWrapper>
      <PaginationControls>
        <Button
          isDisabled={currentPage === 1}
          appearance="subtle"
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Предыдущая
        </Button>
        <PageInfo>
          Страница {currentPage} из {Math.ceil(history.length / itemsPerPage)}
        </PageInfo>
        <Button
          isDisabled={currentPage === Math.ceil(history.length / itemsPerPage)}
          appearance="subtle"
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Следующая
        </Button>
      </PaginationControls>
    </HistoryContainer>
  );
};

// ------------------ Стили ------------------

const HistoryContainer = styled.div`
  margin-top: 24px;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`;

const PageInfo = styled.span`
  margin: 0 16px;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
  overflow-x: auto;

  table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;
    position: relative;

    th,
    td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    th {
      background-color: #f4f4f4;
      position: sticky;
      top: 0;
      z-index: 2;
    }
  }
`;

/** Контейнер для текста заголовка + handle ресайза */
const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 100%;
  padding-right: 10px;

 .react-resizable-handle {
  position: absolute;
  right: -21px;
  top: 50%; /* Центрирование по вертикали */
  width: 15px; /* Узкая ширина для палочки */
  height: 55px; /* Высота ручки */
  background-color: transparent; /* Прозрачный фон */
  border-left: 2px solid #999; /* Вертикальная линия */
  cursor: col-resize; /* Курсор изменения размера */
  transform: translateY(-50%); /* Центровка */
  transition: border-color 0.2s ease; /* Плавное изменение цвета */
}

.react-resizable-handle:hover {
  border-left-color: #444; /* Более насыщенный цвет при наведении */
}
`;

/** Ячейка, которая обрезается многоточием и показывает tooltip при наведении */
const EllipsisTooltipCell = ({ content, color = 'inherit' }) => (
  <Tooltip content={content} position="top">
    <StyledCell style={{ color }}>{content}</StyledCell>
  </Tooltip>
);

const StyledCell = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export default History;
